import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import {
  LoadConfig,
  LoadLatestConfigForProject,
  LoadDeployedConfigForProject,
  SetCurrentConfig,
  SetCreateConfigError,
  DeployConfig,
  SetDeployConfigError,
  SetLoadConfigError,
  LoadConfigSchemaSuccess,
  LoadConfigSchemaFailed,
  DeployConfigSucceeded,
  CreateConfigWithOptionalDeploySucceeded,
  CreateConfigSkipped,
  DeployConfigSkipped,
  DeployPreviewConfigErrored,
  DeployPreviewConfigSucceeded,
  LoadMediaAssetCreatorConfigSucceeded,
  LoadMediaAssetCreatorConfigErrored,
  DeployMediaAssetCreatorConfigSucceeded,
  DeployMediaAssetCreatorConfigErrored,
  DeployMediaAssetCreatorConfigStarted,
  DeployPreviewConfigStarted,
  ConfigActions,
  ConfigEffectActions,
} from './config.actions';
import {
  mergeMap,
  map,
  catchError,
  tap,
  filter,
  withLatestFrom,
  switchMap,
  concatMap,
} from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { ConfigService, PreviewTaskType } from './config.service';
import { Router } from '@angular/router';
import { Config } from './models/config.model';
import { ConfigApiError, ConfigApiErrorTypes } from './config.errors';
import { SetActiveSchema } from '../schema/schema.actions';
import { SchemaService } from '../schema/schema.service';
import {
  LoadProjectSummaries,
  LoadProjectSummary,
  ProjectCreated,
  ProjectUpdated,
} from '../project/project.actions';
import { getCurrentConfig } from './config.selectors';
import { Store } from '@ngrx/store';
import { AppStore } from '../store.model';
import {
  getCurrentProject,
  getProjectSummary,
  selectPreviewProjectId,
} from '../project/project.selectors';
import { getDeploymentSubdomain } from '../user/user.selectors';
import { ProjectVideoActions } from '../project-video/video/project-video.actions';
import { ProjectEditFormDataStabilized } from '../project-edit-form/project-edit-form.actions';
import { ActionCreatorWithProps } from 'src/app/shared/types/action-creator.type';
import { MediaAssetCreatorVideoActions } from '../media-asset-creator/video/media-asset-creator-video.actions';
import { getProjectId } from '../router/router.selectors';
import { isFeatureFlagEnabled } from '../feature-flags/feature-flags.selectors';
import { AnalyticsTrackingEffectCreatorService } from 'src/app/shared/services/effect-creators/analytics-tracking-effect-creator.service';

@Injectable()
export class ConfigEffects {
  loadConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadConfig),
      mergeMap(({ configId }) =>
        this.configService.loadConfig(configId).pipe(
          map((config: Config) => SetCurrentConfig({ config })),
          catchError((error) => of(SetLoadConfigError({ error }))),
        ),
      ),
    ),
  );

  loadSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetActiveSchema),
      mergeMap((action) =>
        this.schemaService.dereferenceSchema(action.schema).pipe(
          map((schema) => LoadConfigSchemaSuccess({ payload: schema })),
          catchError((error) => of(LoadConfigSchemaFailed(error))),
        ),
      ),
    ),
  );

  loadCurrentConfigForProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadLatestConfigForProject),
      mergeMap(({ projectId }) =>
        this.configService.loadLatestConfigForProject(projectId).pipe(
          map((config: Config) =>
            !!config
              ? SetCurrentConfig({ config })
              : SetLoadConfigError({
                  error: new ConfigApiError(ConfigApiErrorTypes.NoConfigForProject, null),
                }),
          ),
          catchError((error) => of(SetLoadConfigError({ error }))),
        ),
      ),
    ),
  );

  loadDeployedConfigForProject$ = this.createLoadDeployedConfigEffect({
    initiatingAction: LoadDeployedConfigForProject,
    succeededAction: SetCurrentConfig,
    erroredAction: SetLoadConfigError,
  });

  loadDeployedConfigForMediaAssetCreatorVideo$ = this.createLoadDeployedConfigEffect({
    initiatingAction: MediaAssetCreatorVideoActions.initializationTriggered,
    succeededAction: LoadMediaAssetCreatorConfigSucceeded,
    erroredAction: LoadMediaAssetCreatorConfigErrored,
  });

  createConfigIfRequiredWithOptionalDeploy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectCreated, ProjectUpdated),
      mergeMap(({ configSettings, configParameters, deploy, redirect }) =>
        configSettings
          ? this.configService.createNewConfig(configSettings, configParameters).pipe(
              map((config) =>
                CreateConfigWithOptionalDeploySucceeded({ config, deploy, redirect }),
              ),
              catchError((err) => {
                if (err.error?.status === 409) {
                  const error = new ConfigApiError(
                    ConfigApiErrorTypes.ConfigConflictError,
                    err.error?.error?.error,
                  );
                  return of(SetCreateConfigError({ error }));
                }
                return of(SetCreateConfigError({ error: err }));
              }),
            )
          : of(CreateConfigSkipped({ deploy, redirect })),
      ),
    ),
  );

  trackProjectCreated$ = this.analyticsTrackingEffectCreatorService.createEffect({
    initiatingAction: ProjectCreated,
    event: { context: 'projectForm', object: 'project', action: 'created' },
    properties: ({ configSettings, templateId }) => ({
      projectId: configSettings.projectId,
      templateId,
    }),
  });

  deployConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeployConfig),
      withLatestFrom(this.store$.select(getDeploymentSubdomain)),
      mergeMap(([action, deploymentSubdomain]) =>
        this.configService.deployConfig(action.config, action.keyName, deploymentSubdomain).pipe(
          map(() => DeployConfigSucceeded({ redirect: `/project/${action.config.projectId}` })),
          catchError((error) => of(SetDeployConfigError({ error }))),
        ),
      ),
    ),
  );

  deployPreviewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProjectEditFormDataStabilized,
        ProjectVideoActions.updatePreviewClicked,
        ProjectVideoActions.reconnectClicked,
      ),
      withLatestFrom(
        this.store$.select(isFeatureFlagEnabled('project-interactive-preview')),
        this.store$.select(selectPreviewProjectId),
      ),
      switchMap(([{ configValues }, interactivePreviewEnabled, previewProjectId]) =>
        this.configService
          .deployPreviewConfig(
            configValues,
            interactivePreviewEnabled ? PreviewTaskType.Interactive : PreviewTaskType.Passive,
            previewProjectId,
          )
          .pipe(
            map((task) => DeployPreviewConfigStarted({ task })),
            catchError((error) => of(DeployPreviewConfigErrored({ error }))),
          ),
      ),
    ),
  );

  pollDeployPreviewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeployPreviewConfigStarted),
      switchMap(({ task }) =>
        this.configService.pollDeployPreviewConfig(task.id).pipe(
          map(({ jwt }) => DeployPreviewConfigSucceeded({ jwt })),
          catchError((error) => of(DeployPreviewConfigErrored({ error }))),
        ),
      ),
    ),
  );

  deployMediaAssetCreatorVideoConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadMediaAssetCreatorConfigSucceeded),
      withLatestFrom(this.store$.select(selectPreviewProjectId)),
      switchMap(([{ config }, previewProjectId]) =>
        this.configService
          .deployPreviewConfig(config.values, PreviewTaskType.Passive, previewProjectId)
          .pipe(
            map((task) => DeployMediaAssetCreatorConfigStarted({ task })),
            catchError((error) => of(DeployMediaAssetCreatorConfigErrored({ error }))),
          ),
      ),
    ),
  );

  pollDeployMediaAssetCreatorVideoConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeployMediaAssetCreatorConfigStarted),
      switchMap(({ task }) =>
        this.configService.pollDeployPreviewConfig(task.id).pipe(
          map(({ jwt }) => DeployMediaAssetCreatorConfigSucceeded({ jwt })),
          catchError((error) => of(DeployMediaAssetCreatorConfigErrored({ error }))),
        ),
      ),
    ),
  );

  deployCurrentConfigIfRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateConfigWithOptionalDeploySucceeded, CreateConfigSkipped),
      withLatestFrom(
        this.store$.select(getCurrentProject),
        this.store$.select(getCurrentConfig),
        this.store$.select(getDeploymentSubdomain),
      ),
      mergeMap(([action, project, config, deploymentSubdomain]) =>
        action.deploy
          ? this.configService.deployConfig(config, project.keyName, deploymentSubdomain).pipe(
              map(() => DeployConfigSucceeded({ redirect: action.redirect })),
              catchError((error) => of(SetDeployConfigError({ error }))),
            )
          : of(DeployConfigSkipped({ redirect: action.redirect })),
      ),
    ),
  );

  undeployCurrentConfigConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigActions.undeployClicked),
      mergeMap(({ projectSummary }) =>
        forkJoin([of(projectSummary), this.configService.showUndeployConfirmation(projectSummary)]),
      ),
      map(([{ deployedConfigId, id }, result]) =>
        result
          ? ConfigActions.undeployConfirmationClicked({ deployedConfigId, projectId: id })
          : ConfigActions.undeployCancellationClicked(),
      ),
      catchError((error) => of(ConfigEffectActions.undeployErrored({ error }))),
    ),
  );

  undeployCurrentConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigActions.undeployConfirmationClicked),
      concatMap(({ deployedConfigId, projectId }) =>
        this.configService.undeployConfig(deployedConfigId).pipe(
          map((task) => ConfigEffectActions.undeployProcessing({ task, projectId })),
          catchError((error) => of(ConfigEffectActions.undeployErrored({ error }))),
        ),
      ),
    ),
  );

  pollUndeployConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigEffectActions.undeployProcessing),
      switchMap(({ task, projectId }) =>
        this.configService.pollUndeployConfig(task.id).pipe(
          map(() => ConfigEffectActions.undeploySucceeded({ projectId })),
          catchError((error) => of(ConfigEffectActions.undeployErrored({ error }))),
        ),
      ),
    ),
  );

  reloadAfterUndeploySucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigEffectActions.undeploySucceeded),
      withLatestFrom(this.store$.select(getProjectSummary)),
      switchMap(([{ projectId }, projectSummary]) =>
        projectSummary
          ? of(LoadProjectSummary({ projectId }))
          : of(LoadProjectSummaries({ query: null })),
      ),
    ),
  );

  redirectAfterProjectSaveOrDeploy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeployConfigSucceeded, DeployConfigSkipped),
        map((action) => action.redirect),
        filter((redirect) => Boolean(redirect)),
        tap((redirect) => this.router.navigate([redirect])),
      ),
    { dispatch: false },
  );

  constructor(
    private store$: Store<AppStore>,
    private actions$: Actions,
    private configService: ConfigService,
    private schemaService: SchemaService,
    private analyticsTrackingEffectCreatorService: AnalyticsTrackingEffectCreatorService,
    private router: Router,
  ) {}

  private createLoadDeployedConfigEffect(options: {
    initiatingAction: ActionCreatorWithProps<{}>;
    succeededAction: ActionCreatorWithProps<{ config: Config }>;
    erroredAction: ActionCreatorWithProps<{ error: ConfigApiError }>;
  }) {
    const { initiatingAction, succeededAction, erroredAction } = options;

    return createEffect(() =>
      this.actions$.pipe(
        ofType(initiatingAction),
        withLatestFrom(this.store$.select(getProjectId)),
        mergeMap(([, projectId]) =>
          this.configService.loadDeployedConfigForProject(projectId).pipe(
            map((config: Config) =>
              !!config
                ? succeededAction({ config })
                : erroredAction({
                    error: new ConfigApiError(ConfigApiErrorTypes.NoConfigForProject, null),
                  }),
            ),
            catchError((error) =>
              of(
                erroredAction({
                  error: new ConfigApiError(ConfigApiErrorTypes.LoadConfigError, error),
                }),
              ),
            ),
          ),
        ),
      ),
    );
  }
}
